import { useEffect } from 'react';

/**
 * Normalizes a keyboard event key across old and new browsers.
 * Returns one of the canonical names used in modal handlers:
 *   'Back', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
 *   'Enter', 'Space', 'MediaPlayPause', or the original key string.
 */
function normalizeModalKey(e: KeyboardEvent): string {
  var k = e.key || '';
  var code = (e as any).keyCode || (e as any).which || 0;

  // Back / remote Back button
  // keyCode 10009 = Tizen 3 (Samsung remote Back)
  // keyCode 461   = LG webOS Back
  if (
    k === 'Escape' || k === 'Esc' ||
    k === 'Back' || k === 'BrowserBack' || k === 'GoBack' ||
    code === 10009 || code === 461 || code === 27
  ) return 'Back';

  if (k === 'ArrowUp'    || k === 'Up'    || code === 38) return 'ArrowUp';
  if (k === 'ArrowDown'  || k === 'Down'  || code === 40) return 'ArrowDown';
  if (k === 'ArrowLeft'  || k === 'Left'  || code === 37) return 'ArrowLeft';
  if (k === 'ArrowRight' || k === 'Right' || code === 39) return 'ArrowRight';

  if (k === 'Enter' || k === 'OK' || k === 'Return' || code === 13) return 'Enter';
  if (k === ' ' || k === 'Spacebar' || k === 'Space' || code === 32) return 'Space';

  if (k === 'MediaPlayPause' || code === 179 || code === 10252) return 'MediaPlayPause';
  if (k === 'MediaPlay'      || code === 415) return 'MediaPlay';
  if (k === 'MediaPause'     || code === 19)  return 'MediaPause';

  return k;
}

type ModalKeyHandler = (normalizedKey: string, originalEvent: KeyboardEvent) => void;

/**
 * Registers a keydown listener that:
 *  - Runs in the capture phase (fires before page-level bubble handlers)
 *  - Calls stopImmediatePropagation() so page-level listeners never receive the event
 *  - Passes a normalized key string to the handler
 *
 * Use this in every modal to prevent navigation keys from leaking to the page behind it.
 *
 * Compatible with Chrome 47 / Tizen 3 (uses boolean `true` for capture, not options object).
 */
export function useModalKeyTrap(handler: ModalKeyHandler, enabled: boolean = true): void {
  useEffect(function () {
    if (!enabled) return;
    function onKeyDown(e: KeyboardEvent): void {
      e.stopImmediatePropagation();
      var normalized = normalizeModalKey(e);
      handler(normalized, e);
    }
    window.addEventListener('keydown', onKeyDown, true);
    return function () {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [handler, enabled]);
}
